import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { ProgramInfiniteTypeOf } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOffTypeOf } from "@modules/programs/programOnOffTypes"
import { useToast } from "@modules/ui/components/huToast"
import { verboseDateIntervalFormat } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import { useObservable } from "micro-observables"

export const useProgramSessionDuplicate = () => {
  const { programSessionService } = useServicesContext()
  const toast = useToast()
  const language = useLanguage()
  const weekCopied = useObservable(programSessionService.copyWeek)

  const onCopyInfiniteProgramWeek = (
    programId: string,
    programType: typeof ProgramInfiniteTypeOf,
    programName: string,
    originStartDate: string,
    originEndDate: string,
  ) => {
    programSessionService.setInfiniteWeekToCopy(programType, programId, programName, originStartDate, originEndDate)

    toast.show({ severity: "success", detail: word("program.weekCopyPaste.copy.success") })
  }

  const onPasteInfiniteProgramWeek = async (
    destinationProgramId: string,
    destinationStartDate: string,
    overwrite?: boolean,
  ) => {
    if (weekCopied && weekCopied.programType === ProgramInfiniteTypeOf) {
      try {
        await programSessionService.copyWeekFromAnInfiniteProgramToAnother(
          weekCopied.originProgramId,
          destinationProgramId,
          weekCopied.originStartDate,
          weekCopied.originEndDate,
          destinationStartDate,
          overwrite,
        )

        const formattedDates = verboseDateIntervalFormat(
          dayjs(weekCopied.originStartDate),
          dayjs(weekCopied.originEndDate),
          language,
        )

        toast.show({ severity: "success", detail: word("program.weekCopyPaste.paste.success", { formattedDates }) })

        programSessionService.clearWeekToCopy()
      } catch (e: any) {
        toast.show({ severity: "error", summary: word("program.weekCopyPaste.paste.error"), detail: e.message })
      }
    }
  }

  const onCopyOnOffProgramWeek = (
    programId: string,
    programType: typeof ProgramOnOffTypeOf,
    programName: string,
    originStartIndex: number,
    originEndIndex: number,
  ) => {
    programSessionService.setOnOffWeekToCopy(programType, programId, programName, originStartIndex, originEndIndex)

    toast.show({ severity: "success", detail: word("program.weekCopyPaste.copy.success") })
  }

  const onPasteOnOffProgramWeek = async (
    destinationProgramId: string,
    destinationStartIndex: number,
    overwrite?: boolean,
  ) => {
    if (weekCopied && weekCopied.programType === ProgramOnOffTypeOf) {
      try {
        await programSessionService.copyWeekFromAnOnOffProgramToAnother(
          weekCopied.originProgramId,
          destinationProgramId,
          weekCopied.originStartIndex,
          weekCopied.originEndIndex,
          destinationStartIndex,
          overwrite,
        )

        const formattedDates =
          `${word("program.onOff.dayIndex.title")} ${weekCopied.originStartIndex} - ${word("program.onOff.dayIndex.title")} ${weekCopied.originEndIndex}`.toLocaleLowerCase()

        toast.show({ severity: "success", detail: word("program.weekCopyPaste.paste.success", { formattedDates }) })

        programSessionService.clearWeekToCopy()
      } catch (e: any) {
        toast.show({ severity: "error", summary: word("program.weekCopyPaste.paste.error"), detail: e.message })
      }
    }
  }

  return {
    onCopyInfiniteProgramWeek,
    onPasteInfiniteProgramWeek,
    onCopyOnOffProgramWeek,
    onPasteOnOffProgramWeek,
    weekCopied,
  }
}
